import type { Step, FlowConfigOutput, FlowItemOutput, ItemImage, TranslationLocale } from "../types/configurator";
import type { HubSpotProduct } from "../types/configurator";
import { stepId, itemId } from "../utils";

export type FieldValueTranslations = Partial<Record<TranslationLocale, { description?: string }>>;
export type ProductTranslations = Partial<Record<TranslationLocale, { name?: string }>>;

const API_BASE = import.meta.env.VITE_API_BASE || "/api";
const ADMIN_API_KEY = import.meta.env.VITE_ADMIN_TOKEN || "";

const adminHeaders = {
  "Authorization": `Bearer ${ADMIN_API_KEY}`,
};

/**
 * List all flows.
 * GET /api/flows → { id, name, updated_at }[]
 */
export async function fetchFlows(): Promise<{ id: string; name: string; updated_at: number }[]> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) return [];
  const res = await fetch(`${API_BASE}/api/flows`, { headers: adminHeaders });
  if (!res.ok) throw new Error(`Failed to fetch flows: ${res.status}`);
  return res.json();
}

/**
 * Create a new flow.
 * POST /api/flows → { ok: true }
 */
export async function createFlow(name: string, id: string): Promise<string> {
  const res = await fetch(`${API_BASE}/api/flows`, {
    method: 'POST',
    headers: { ...adminHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, name }),
  });
  if (!res.ok) throw new Error(`Failed to create flow: ${res.status}`);
  const data = await res.json() as { ok: boolean; id: string };
  return data.id;
}

/**
 * Delete a flow by ID.
 * DELETE /api/flows/:id
 */
export async function deleteFlow(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/flows/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: adminHeaders,
  });
  if (res.status === 404) throw new Error('Flow not found');
  if (!res.ok) throw new Error(`Failed to delete flow: ${res.status}`);
}

/**
 * Save the raw step state to D1 via the worker.
 * POST /api/flows/:configuratorId
 */
export async function saveFlow(
  configuratorId: string,
  steps: Step[],
  name?: string,
): Promise<void> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    console.log("[DEV] Would save flow:", steps);
    return;
  }

  const res = await fetch(`${API_BASE}/api/flows/${configuratorId}`, {
    method: "POST",
    headers: { ...adminHeaders, "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ steps, ...(name !== undefined && { name }) }),
  });

  if (!res.ok) throw new Error(`Failed to save flow: ${res.status}`);
}

/**
 * Load the raw step state from D1 via the worker.
 * GET /api/flows/:configuratorId
 */
export async function loadFlow(configuratorId: string): Promise<{ steps: Step[]; name: string } | null> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return null;
  }

  const res = await fetch(`${API_BASE}/api/flows/${configuratorId}`, {
    credentials: "include",
    headers: { ...adminHeaders, 'Cache-Control': 'no-cache' },
  });

  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load flow: ${res.status}`);
  const data = await res.json();
  const raw = data.steps ?? null;
  if (!raw) return null;

  // If steps already have string IDs (admin/raw format), return as-is
  if (raw[0]?.id || raw.length === 0) {
    return { steps: raw, name: data.name ?? '' };
  }

  // Otherwise normalize from enriched (customer-facing) format
  const normalized = raw.map((s: any) => ({
    id: stepId(),
    title: s.title || "",
    description: s.description || "",
    breadcrumb: s.breadcrumb || "",
    proTip: s.proTip || "",
    selectionType: s.selectionType || "single",
    required: s.required ?? true,
    conditions: Array.isArray(s.conditions)
      ? s.conditions
      : (s.showWhen ? [s.showWhen] : []) as Step["conditions"],
    exclusions: Array.isArray(s.exclusions) ? s.exclusions : [],
    items: (s.items || []).map((item: any) => {
      if (item.type === "product" || item.itemType === "product") {
        return { uid: itemId(), type: "product" as const, productId: item.productId || item.hubspotProductId };
      }
      return { uid: itemId(), type: "field_value" as const, field: item.field, value: item.value, label: item.label };
    }).filter(Boolean),
  }));

  // Rebuild showWhen references using the new string IDs
  raw.forEach((s: any, i: number) => {
    if (s.showWhen?.stepId != null) {
      const refIdx = typeof s.showWhen.stepId === "number" ? s.showWhen.stepId - 1 : raw.findIndex((r: any) => r.id === s.showWhen.stepId);
      if (refIdx >= 0 && refIdx < normalized.length) {
        normalized[i].showWhen = { stepId: normalized[refIdx].id, selectedAny: s.showWhen.selectedAny || [] };
      }
    }
  });

  return { steps: normalized, name: data.name ?? '' };
}

/**
 * Fetch all image assignments.
 * GET /api/images → Record<"field:value", imageUrl>
 */
export async function fetchImages(): Promise<Record<string, string>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/images`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch images: ${res.status}`);
  return res.json();
}

/**
 * Upload an image and assign it to a field value.
 * POST /api/images/upload → { imageUrl }
 */
export async function uploadImage(field: string, value: string, file: File): Promise<string> {
  const form = new FormData();
  form.append('field', field);
  form.append('value', value);
  form.append('file', file);

  const res = await fetch(`${API_BASE}/api/images/upload`, {
    method: 'POST',
    headers: adminHeaders,
    body: form,
  });

  if (!res.ok) throw new Error(`Failed to upload image: ${res.status}`);
  const data = await res.json() as { imageUrl: string };
  return data.imageUrl;
}

/**
 * Delete the image assigned to a field value.
 * DELETE /api/images/:field/:value
 */
export async function deleteImage(field: string, value: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/images/${encodeURIComponent(field)}/${encodeURIComponent(value)}`,
    { method: 'DELETE', headers: adminHeaders }
  );

  if (!res.ok) throw new Error(`Failed to delete image: ${res.status}`);
}

/**
 * Fetch all item images.
 * GET /api/item-images → Record<itemKey, ItemImage[]>
 */
export async function fetchItemImages(): Promise<Record<string, ItemImage[]>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/item-images`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch item images: ${res.status}`);
  return res.json();
}

/**
 * Upload an image for an item.
 * POST /api/item-images/upload → { id, imageUrl }
 */
export async function uploadItemImage(itemKey: string, file: File): Promise<ItemImage> {
  const form = new FormData();
  form.append('itemKey', itemKey);
  form.append('file', file);

  const res = await fetch(`${API_BASE}/api/item-images/upload`, {
    method: 'POST',
    headers: adminHeaders,
    body: form,
  });

  if (!res.ok) throw new Error(`Failed to upload item image: ${res.status}`);
  const data = await res.json() as { id: string; imageUrl: string };
  return { id: data.id, url: data.imageUrl };
}

/**
 * Delete an item image by ID.
 * DELETE /api/item-images/:imageId
 */
export async function deleteItemImage(imageId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/item-images/${encodeURIComponent(imageId)}`,
    { method: 'DELETE', headers: adminHeaders }
  );

  if (!res.ok) throw new Error(`Failed to delete item image: ${res.status}`);
}

/**
 * Fetch all field value descriptions.
 * GET /api/field-value-descriptions → Record<"field:value", description>
 */
export async function fetchDescriptions(): Promise<Record<string, string>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/field-value-descriptions`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch descriptions: ${res.status}`);
  return res.json();
}

/**
 * Save a description for a field value.
 * POST /api/field-value-descriptions
 */
export async function saveDescription(field: string, value: string, description: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/field-value-descriptions`, {
    method: 'POST',
    headers: { ...adminHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, value, description }),
  });

  if (!res.ok) throw new Error(`Failed to save description: ${res.status}`);
}

/**
 * Delete the description for a field value.
 * DELETE /api/field-value-descriptions/:field/:value
 */
export async function deleteDescription(field: string, value: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/field-value-descriptions/${encodeURIComponent(field)}/${encodeURIComponent(value)}`,
    { method: 'DELETE', headers: adminHeaders }
  );

  if (!res.ok) throw new Error(`Failed to delete description: ${res.status}`);
}

/**
 * Fetch all field value translations.
 * GET /api/field-value-translations → Record<"field:value", FieldValueTranslations>
 */
export async function fetchFieldValueTranslations(): Promise<Record<string, FieldValueTranslations>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/field-value-translations`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch translations: ${res.status}`);
  return res.json();
}

/**
 * Save all translations for a field value. Empty payload deletes the row.
 * POST /api/field-value-translations
 */
export async function saveFieldValueTranslations(
  field: string,
  value: string,
  translations: FieldValueTranslations,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/field-value-translations`, {
    method: 'POST',
    headers: { ...adminHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, value, translations }),
  });

  if (!res.ok) throw new Error(`Failed to save translations: ${res.status}`);
}

/**
 * Delete all translations for a field value.
 * DELETE /api/field-value-translations/:field/:value
 */
export async function deleteFieldValueTranslations(field: string, value: string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/field-value-translations/${encodeURIComponent(field)}/${encodeURIComponent(value)}`,
    { method: 'DELETE', headers: adminHeaders }
  );

  if (!res.ok) throw new Error(`Failed to delete translations: ${res.status}`);
}

/**
 * Fetch all field value badges.
 * GET /api/field-value-badges → Record<"field:value", featured_product option values>
 */
export async function fetchFieldValueBadges(): Promise<Record<string, string[]>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/field-value-badges`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch badges: ${res.status}`);
  return res.json();
}

/**
 * Save the badges for a field value. An empty list deletes the row.
 * POST /api/field-value-badges
 */
export async function saveFieldValueBadges(field: string, value: string, badges: string[]): Promise<void> {
  const res = await fetch(`${API_BASE}/api/field-value-badges`, {
    method: 'POST',
    headers: { ...adminHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, value, badges }),
  });

  if (!res.ok) throw new Error(`Failed to save badges: ${res.status}`);
}

/**
 * Fetch all product translations.
 * GET /api/product-translations → Record<hubspotProductId, ProductTranslations>
 */
export async function fetchProductTranslations(): Promise<Record<string, ProductTranslations>> {
  if (import.meta.env.DEV && !import.meta.env.VITE_API_BASE) {
    return {};
  }

  const res = await fetch(`${API_BASE}/api/product-translations`, {
    headers: { 'Cache-Control': 'no-cache' },
  });

  if (!res.ok) throw new Error(`Failed to fetch product translations: ${res.status}`);
  return res.json();
}

/**
 * Save all translations for a product. Empty payload deletes the row.
 * POST /api/product-translations
 */
export async function saveProductTranslations(
  productId: string,
  translations: ProductTranslations,
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/product-translations`, {
    method: 'POST',
    headers: { ...adminHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({ productId, translations }),
  });

  if (!res.ok) throw new Error(`Failed to save product translations: ${res.status}`);
}

/**
 * Build the JSON output that the customer-facing configurator will consume.
 */
export function buildFlowOutput(
  configuratorId: string,
  steps: Step[],
  productMap: Record<string, HubSpotProduct>,
  itemImageMap: Record<string, ItemImage[]> = {},
): FlowConfigOutput {
  return {
    configuratorId,
    version: 1,
    steps: steps.map((s, idx) => {
      const base: any = {
        stepId: s.id,
        title: s.title || `Step ${idx + 1}`,
        description: s.description || "",
        breadcrumb: s.breadcrumb || "",
        proTip: s.proTip || "",
        selectionType: s.selectionType,
        required: s.required ?? true,
        items: s.items
          .map((item): FlowItemOutput | null => {
            if (item.type === 'product') {
              const p = productMap[item.productId];
              if (!p) return null;
              const adminImgs = (itemImageMap[`product:${item.productId}`] ?? []).map((i) => i.url);
              const images = [p.imageUrl, ...adminImgs].filter(Boolean) as string[];
              return {
                itemType: 'product',
                hubspotProductId: item.productId,
                label: p.name,
                images: images.length > 0 ? images : undefined,
                sku: p.sku,
                price: p.price,
                subCategory: p.subCategory,
                shortDescription: p.shortDescription,
              };
            } else {
              const imgs = (itemImageMap[`field_value:${item.field}:${item.value}`] ?? []).map((i) => i.url);
              return {
                itemType: 'field_value',
                field: item.field,
                value: item.value,
                label: item.label,
                images: imgs.length > 0 ? imgs : undefined,
              };
            }
          })
          .filter(Boolean) as FlowItemOutput[],
      };

      const validConditions = (s.conditions ?? []).filter(
        (c) => steps.some((rs) => rs.id === c.stepId)
      );
      if (validConditions.length > 0) base.conditions = validConditions;

      const validExclusions = (s.exclusions ?? []).filter(
        (e) => steps.some((rs) => rs.id === e.stepId)
      );
      if (validExclusions.length > 0) base.exclusions = validExclusions;

      return base;
    }),
  };
}
